import React, { useContext } from "react";
import { Check, X } from "lucide-react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";

/**
 * Live feedback for the five rules every signup flow already enforces
 * (see isStrongPassword in registerPage / registerShop / registerStaff /
 * forgotPasswordConfirm). Showing them as you type beats failing the
 * whole form on submit.
 *
 * Copy lives under translations[language].register so the four flows
 * share one set of strings.
 */
export const PASSWORD_RULES = [
	{ key: "length", test: (p) => p.length >= 8 },
	{ key: "upper", test: (p) => /[A-Z]/.test(p) },
	{ key: "lower", test: (p) => /[a-z]/.test(p) },
	{ key: "number", test: (p) => /[0-9]/.test(p) },
	{ key: "symbol", test: (p) => /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(p) },
];

const PasswordStrength = ({ value = "", confirmValue = null }) => {
	const { language } = useContext(CombinedContext);
	const t = translations[language].register;

	if (!value) return null;

	const met = PASSWORD_RULES.filter((r) => r.test(value));
	const score = met.length;
	const tier = score >= 5 ? "strong" : score >= 3 ? "fair" : "weak";

	return (
		<div className="sf-auth-strength">
			<div className="sf-auth-strength-track" aria-hidden="true">
				{PASSWORD_RULES.map((rule, i) => (
					<span key={rule.key} className={`sf-auth-strength-seg${i < score ? ` is-on-${tier}` : ""}`} />
				))}
			</div>

			<p className="sf-auth-strength-label" aria-live="polite">
				{t.strength[tier]}
			</p>

			<ul className="sf-auth-rules">
				{PASSWORD_RULES.map((rule) => (
					<li key={rule.key} className={`sf-auth-rule${rule.test(value) ? " is-met" : ""}`}>
						{t.rules[rule.key]}
					</li>
				))}
			</ul>

			{confirmValue !== null && confirmValue !== "" && (
				<p className={`sf-auth-match ${value === confirmValue ? "is-ok" : "is-bad"}`} aria-live="polite">
					{value === confirmValue ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
					{value === confirmValue ? t.match.ok : t.match.bad}
				</p>
			)}
		</div>
	);
};

export default PasswordStrength;
